/**
 * DiffBoardPane — the workbench's diff primitive.
 *
 * Renders the visual diff (the `diff/` engine: split / overlay diagram of the
 * live workspace vs the compare target) from the store + CompareProvider. The
 * viewer mounts it in place of its old bespoke `<DiffBoard …prop-bag/>`; compare
 * state comes from `useCompare`, the current files from the store, and "load
 * into workspace" routes through `store.replaceFiles`. Returns null when not
 * comparing.
 *
 * The diff diagram is a specialized dual-pane/union renderer, so it keeps its
 * own InfiniteBoard-based implementation (not BoardPane) — this primitive just
 * binds it to the workspace services.
 */
import type { ReactElement } from "react";
import { DiffBoard } from "../../diff/DiffBoard.js";
import type { DiffState } from "../../diff/useDiff.js";
import { useWorkspace } from "../WorkspaceContext.js";
import { useWorkspaceFiles } from "../WorkspaceStore.js";
import { useCompare } from "../CompareContext.js";

export interface DiffBoardPaneProps {
  /** Display label for the live workspace side (the store has no source label). */
  readonly currentLabel: string;
  /**
   * When true: DiffBoard hides its own toolbar (SourceMenu / before‑after
   * chips / exit button) and internal DiffNav column. The outer Studio chrome
   * (TopBar mode tabs + WorkspaceNav diff source) takes over those roles.
   */
  readonly studioMode?: boolean;
  /**
   * Pre-computed diff from a server-side endpoint. Pass this to avoid the
   * expensive client-side model diff pipeline (needed for proposed changesets
   * where the compare target equals the working files — i.e. no drafts).
   * VFS maps (trunkVfs / working files) are still used for document diff.
   */
  readonly precomputedDiff?: DiffState;
  /**
   * Trunk VFS for document diff in studio review mode. The before VFS for
   * MarkdownDiffView; the after VFS is the current working files. Without
   * this, Documents mode would compare identical VFS maps (both = feature
   * branch) and show no document changes.
   */
  readonly trunkVfs?: ReadonlyMap<string, string>;
}

export function DiffBoardPane({
  currentLabel,
  studioMode,
  precomputedDiff,
  trunkVfs,
}: DiffBoardPaneProps): ReactElement | null {
  const store = useWorkspace();
  const files = useWorkspaceFiles(store);
  const { against, swapped, setCompareAgainst, swapCompare, clearCompare } =
    useCompare();
  if (!against) return null;

  const current = { vfs: files, label: currentLabel };

  // In studio review mode with trunk VFS, use trunk as "before" and the
  // working files (feature branch) as "after" for correct document diff
  // direction (trunk → feature branch = the proposed change).
  const before = studioMode && trunkVfs
    ? { vfs: trunkVfs, label: "trunk" }
    : (swapped ? against : current);
  const after = studioMode && trunkVfs
    ? current
    : (swapped ? current : against);

  return (
    <DiffBoard
      before={before}
      after={after}
      currentSources={current}
      compareAgainst={against}
      compareSwapped={swapped}
      onSetCompare={setCompareAgainst}
      onSwap={swapCompare}
      onClear={clearCompare}
      onLoadIntoWorkspace={(slot) => store.replaceFiles(slot.vfs)}
      studioMode={studioMode}
      precomputedDiff={precomputedDiff}
    />
  );
}
